import { useMemo, useState } from 'react';
import { AlertTriangle, ChevronRight, Printer, RefreshCcw, ShieldAlert, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Fact, QuietEmpty, StoredImage, WorkerChip } from './shared';
import { SignoffStateBadge } from './EmployeeMatrixViews';
import {
  SIGNOFF_STATE_META,
  signoffState,
  useRevokeSignoff,
  useSignoffImageUrls,
  useWorkstationSignoff,
  type SignoffState,
} from '@/features/workMode/signoffs';
import { describeWorkError } from '@/features/workMode/api';
import { formatDateOnly, formatDateTime } from '@/lib/formatters';
import { printDocument, type PrintDocumentSection } from '@/lib/printDocument';
import type {
  SignoffInstructionSet,
  StationInstructionHash,
  WorkstationSignoff,
  WorkstationSignoffWithCopy,
} from '@/features/workMode/types';
import type { WorkLookups } from '@/features/workMode/useLookups';

function stationLabel(s: WorkstationSignoff, lookups: WorkLookups): string {
  return (s.workstation_id && lookups.stations.get(s.workstation_id)?.name) || s.workstation_name;
}

function printSignoff(
  signoff: WorkstationSignoffWithCopy,
  state: SignoffState,
  lookups: WorkLookups,
  urls: Map<string, string> | undefined,
) {
  const person = lookups.personName(signoff.user_id);
  const station = stationLabel(signoff, lookups);
  const sections: PrintDocumentSection[] = [
    {
      title: 'Sign-off',
      fields: [
        { label: 'Employee', value: person },
        { label: 'Workstation', value: station },
        { label: 'Signed off on', value: formatDateOnly(signoff.signed_off_on) },
        { label: 'Signed off by', value: signoff.signed_off_by ? lookups.personName(signoff.signed_off_by) : null },
        { label: 'Recorded', value: formatDateTime(signoff.created_at) },
        { label: 'Status', value: SIGNOFF_STATE_META[state].label },
        ...(signoff.revoked_at
          ? [
              { label: 'Revoked', value: formatDateTime(signoff.revoked_at) },
              { label: 'Revoked by', value: signoff.revoked_by ? lookups.personName(signoff.revoked_by) : null },
              { label: 'Reason', value: signoff.revoke_reason },
            ]
          : []),
      ],
    },
    ...(signoff.notes ? [{ title: 'Notes', text: signoff.notes }] : []),
  ];
  if (signoff.instructions.length === 0) {
    sections.push({ title: 'Work instructions', text: 'No work instructions were on file for this station at sign-off.' });
  }
  for (const set of signoff.instructions) {
    sections.push({
      title: set.title,
      fields: [
        { label: 'Item', value: [set.item_name, set.item_sku].filter(Boolean).join(' · ') },
        { label: 'Version', value: `v${set.version}` },
        { label: 'Steps', value: set.steps.length },
      ],
      text: set.summary,
    });
    if (set.safety_notes) sections.push({ title: 'Safety notes', text: set.safety_notes });
    for (const st of set.steps) {
      const src = st.image_path ? urls?.get(st.image_path) : undefined;
      sections.push({
        title: `Step ${st.step_number} · ${st.title}`,
        text: [st.body, st.caution ? `Caution: ${st.caution}` : null].filter(Boolean).join('\n\n') || null,
        image: src ? { src, alt: st.title } : null,
      });
    }
  }
  const opened = printDocument({
    title: 'Workstation sign-off',
    subtitle: `${person} · ${station}`,
    status: SIGNOFF_STATE_META[state].label,
    sections,
  });
  if (!opened) toast.error('Allow pop-ups to print or save this sign-off as a PDF.');
}

/**
 * One person's sign-off at one station: who signed them off and when, the
 * copy of the instructions they were signed off on, and earlier sign-offs
 * for the same pair.
 */
export function SignoffSheet({
  signoffId,
  allSignoffs,
  lookups,
  hashes,
  canManage,
  onOpenChange,
  onSelect,
  onSignOffAgain,
}: {
  signoffId: string | null;
  allSignoffs: WorkstationSignoff[];
  lookups: WorkLookups;
  hashes: Map<string, StationInstructionHash> | undefined;
  canManage: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (id: string) => void;
  onSignOffAgain: (stationId: string, userId: string) => void;
}) {
  // Keep rendering the last record while the sheet animates closed.
  const [shownId, setShownId] = useState(signoffId);
  if (signoffId && signoffId !== shownId) setShownId(signoffId);
  const { data: signoff, isLoading, isError } = useWorkstationSignoff(shownId);
  const [revoking, setRevoking] = useState(false);
  const [zoom, setZoom] = useState<{ path: string; alt: string } | null>(null);

  const imagePaths = useMemo(
    () => (signoff?.instructions ?? []).flatMap((set) => set.steps.map((st) => st.image_path).filter((p): p is string => !!p)),
    [signoff],
  );
  const { data: urls, isLoading: urlsLoading } = useSignoffImageUrls(imagePaths);

  const history = useMemo(() => {
    if (!signoff) return [];
    return allSignoffs.filter(
      (s) =>
        s.id !== signoff.id &&
        s.user_id === signoff.user_id &&
        s.workstation_id === signoff.workstation_id &&
        (s.workstation_id !== null || s.workstation_name === signoff.workstation_name),
    );
  }, [allSignoffs, signoff]);

  const ready = !!signoff && signoff.id === shownId;
  const state = ready ? signoffState(signoff, hashes) : null;
  const person = ready ? lookups.person(signoff.user_id) : null;
  const station = ready ? stationLabel(signoff, lookups) : '';
  const isActive = ready && !signoff.revoked_at;

  return (
    <Sheet open={!!signoffId} onOpenChange={onOpenChange}>
      <SheetContent>
        {!ready || !state || !person ? (
          <>
            <SheetHeader>
              <SheetTitle>Sign-off</SheetTitle>
            </SheetHeader>
            <SheetBody>{isError && !isLoading ? <QuietEmpty>This sign-off could not be loaded.</QuietEmpty> : <Skeleton className="h-72" />}</SheetBody>
          </>
        ) : (
          <>
            <SheetHeader className="pr-12">
              <div>
                <SignoffStateBadge state={state} />
              </div>
              <WorkerChip person={person} sub={station} size={48} />
              <SheetTitle className="sr-only">
                {person.name} · {station}
              </SheetTitle>
              <SheetDescription>
                Signed off {formatDateOnly(signoff.signed_off_on)}
                {signoff.signed_off_by ? ` by ${lookups.personName(signoff.signed_off_by)}` : ''}
              </SheetDescription>
            </SheetHeader>

            <SheetBody className="space-y-6">
              {state === 'changed' && (
                <p className="flex items-start gap-2 rounded-xl border border-fresh-salmon/60 bg-fresh-salmon/15 px-4 py-3 text-sm text-fresh-salmon-dark">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  The work instructions at this station have changed since this sign-off.
                </p>
              )}

              <dl className="grid grid-cols-2 gap-4">
                <Fact label="Workstation">{station}</Fact>
                <Fact label="Signed off on">{formatDateOnly(signoff.signed_off_on)}</Fact>
                <Fact label="Signed off by">{signoff.signed_off_by ? lookups.personName(signoff.signed_off_by) : '—'}</Fact>
                <Fact label="Recorded">{formatDateTime(signoff.created_at)}</Fact>
                {signoff.revoked_at && (
                  <>
                    <Fact label={signoff.superseded_by ? 'Replaced' : 'Revoked'}>{formatDateTime(signoff.revoked_at)}</Fact>
                    <Fact label="By">{signoff.revoked_by ? lookups.personName(signoff.revoked_by) : '—'}</Fact>
                    {signoff.revoke_reason && (
                      <Fact label="Reason" className="col-span-2">
                        {signoff.revoke_reason}
                      </Fact>
                    )}
                  </>
                )}
                {signoff.notes && (
                  <Fact label="Notes" className="col-span-2">
                    <span className="whitespace-pre-line">{signoff.notes}</span>
                  </Fact>
                )}
              </dl>

              <section className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">
                  Signed-off work instructions <span className="text-muted-foreground">({signoff.instructions.length})</span>
                </h3>
                {signoff.instructions.length === 0 ? (
                  <QuietEmpty>No work instructions were on file for this station at sign-off.</QuietEmpty>
                ) : (
                  signoff.instructions.map((set) => (
                    <InstructionCopy key={set.instruction_id} set={set} onZoom={(path, alt) => setZoom({ path, alt })} />
                  ))
                )}
              </section>

              {history.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-medium text-foreground">Other sign-offs here</h3>
                  <ul className="divide-y divide-border rounded-xl border border-border">
                    {history.map((h) => {
                      const hState = signoffState(h, hashes);
                      return (
                        <li key={h.id}>
                          <button
                            type="button"
                            onClick={() => onSelect(h.id)}
                            className="flex min-h-11 w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-secondary/60"
                          >
                            <span className="min-w-0">
                              <span className="block text-foreground">Signed off {formatDateOnly(h.signed_off_on)}</span>
                              <span className="block truncate text-xs text-muted-foreground">
                                {h.signed_off_by ? `by ${lookups.personName(h.signed_off_by)}` : ''}
                                {h.revoked_at && !h.superseded_by && h.revoke_reason ? ` · ${h.revoke_reason}` : ''}
                              </span>
                            </span>
                            <span className="flex shrink-0 items-center gap-2">
                              <SignoffStateBadge state={hState} />
                              <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
            </SheetBody>

            <SheetFooter>
              <Button
                variant="outline"
                onClick={() => printSignoff(signoff, state, lookups, urls)}
                disabled={imagePaths.length > 0 && urlsLoading}
              >
                <Printer className="h-4 w-4" />
                Print / PDF
              </Button>
              {canManage && isActive && (
                <>
                  <Button variant="ghost" className="text-fresh-salmon-dark" onClick={() => setRevoking(true)}>
                    <UserX className="h-4 w-4" />
                    Revoke
                  </Button>
                  {signoff.workstation_id && (
                    <Button onClick={() => onSignOffAgain(signoff.workstation_id!, signoff.user_id)}>
                      <RefreshCcw className="h-4 w-4" />
                      Sign off again
                    </Button>
                  )}
                </>
              )}
            </SheetFooter>

            {revoking && (
              <RevokeDialog
                signoff={signoff}
                label={`${person.name} at ${station}`}
                onClose={() => setRevoking(false)}
              />
            )}
          </>
        )}

        <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
          <DialogContent className="max-w-4xl p-3">
            <DialogTitle className="sr-only">{zoom?.alt ?? 'Photo'}</DialogTitle>
            <DialogDescription className="sr-only">Step photo from the signed-off work instructions.</DialogDescription>
            {zoom && (
              <StoredImage
                bucket="work-instructions"
                path={zoom.path}
                alt={zoom.alt}
                className="max-h-[80vh] w-full rounded-lg bg-secondary object-contain"
              />
            )}
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

function InstructionCopy({ set, onZoom }: { set: SignoffInstructionSet; onZoom: (path: string, alt: string) => void }) {
  return (
    <article className="overflow-hidden rounded-2xl border border-border">
      <header className="space-y-1 border-b border-border bg-secondary/30 px-4 py-3">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {[set.item_name, set.item_sku, `v${set.version}`].filter(Boolean).join(' · ')}
        </p>
        <h4 className="text-sm font-medium text-foreground">{set.title}</h4>
        {set.summary && <p className="whitespace-pre-line text-sm text-muted-foreground">{set.summary}</p>}
      </header>
      {set.safety_notes && (
        <p className="mx-4 mt-3 flex items-start gap-2 rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/15 px-3 py-2 text-sm text-fresh-salmon-dark">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="whitespace-pre-line">{set.safety_notes}</span>
        </p>
      )}
      {set.steps.length === 0 ? (
        <p className="px-4 py-4 text-sm text-muted-foreground">This set had no steps.</p>
      ) : (
        <ol className="divide-y divide-border">
          {set.steps.map((st) => (
            <li key={st.id} className="flex gap-3 px-4 py-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                {st.step_number}
              </span>
              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-sm font-medium text-foreground">{st.title}</p>
                {st.body && <p className="whitespace-pre-line text-sm text-muted-foreground">{st.body}</p>}
                {st.caution && (
                  <p className="flex items-start gap-2 rounded-lg border border-fresh-salmon/60 bg-fresh-salmon/15 px-3 py-2 text-sm text-fresh-salmon-dark">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{st.caution}</span>
                  </p>
                )}
                {st.image_path && (
                  <StoredImage
                    bucket="work-instructions"
                    path={st.image_path}
                    alt={st.title}
                    className="h-44 w-full max-w-sm bg-secondary object-contain"
                    onClick={() => onZoom(st.image_path!, st.title)}
                  />
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}

function RevokeDialog({ signoff, label, onClose }: { signoff: WorkstationSignoff; label: string; onClose: () => void }) {
  const revoke = useRevokeSignoff();
  const [reason, setReason] = useState('');
  const submit = () => {
    revoke.mutate(
      { id: signoff.id, reason },
      {
        onSuccess: () => {
          toast.success(`Sign-off revoked for ${label}.`);
          onClose();
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Revoke sign-off</DialogTitle>
          <DialogDescription className="sr-only">The sign-off stays in the history as revoked.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="revoke-reason">Reason</Label>
          <Textarea
            id="revoke-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Needs retraining, moved to another line…"
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={revoke.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={submit} disabled={revoke.isPending}>
            Revoke
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
